import { useCallback, useRef } from 'react';
import { dateFormat } from 'utils/shared/utils';
import { EcommerceImpression, EcommercePurchase } from 'ts/shared/interfaces/Ecommerce';
import { getDaysDifference } from 'utils/utils';
import { getBounds } from 'utils/dynamic/dynamic-fl';
import { DynamicEventResponse } from 'ts/dynamic/interfaces/DynamicResponse';
import { useSearchParams } from 'react-router-dom';

const useDataLayer = () => {
  const ecommerceFlag = useRef(localStorage.getItem('__e_comerce'));
  const [urlParams] = useSearchParams();
  const initDataLayer = useCallback(() => {
    if (typeof window !== 'undefined' && !window.dataLayer) {
      window.dataLayer = [];
    }
  }, []);

  const pushImpression = useCallback(
    (dyn: DynamicEventResponse) => {
      if (!ecommerceFlag.current) {
        return;
      }
      const token = urlParams.get('searchToken');
      const areImpressions = sessionStorage.getItem('dynImpressions' + token);
      if (areImpressions) {
        return;
      }
      initDataLayer();
      try {
        if (typeof window !== 'undefined' && window.dataLayer) {
          const departureDate = urlParams.get('departureDate') || '';
          const arrivalDate = urlParams.get('arrivalDate') || '';
          const nights = getDaysDifference(departureDate, arrivalDate);
          const impressions = dyn.dynamicHotel?.allHotels.map((item: any, index: number) => ({
            id: item.item.code,
            name: item.item.name,
            category: 'FLT+HTL',
            price: Number(item.roomClasses[0].price.value.toFixed(2)),
            brand: 'FLT+HTL',
            variant: 'הלוך חזור',
            list: 'תוצאות חיפוש',
            position: index,
            dimension1: 0,
            dimension2: nights,
            dimension3: '',
          }));

          window.dataLayer.push({
            event: 'impression',
            ecommerce: {
              impressions,
            },
          } as EcommerceImpression);

          sessionStorage.setItem('dynImpressions' + token, '1');
        }
      } catch (error) {
        console.error('Error pushing impression to dataLayer:', error);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [initDataLayer]
  );

  const pushDynamicProductDetails = useCallback(
    (dynamicData: DynamicEventResponse) => {
      if (!ecommerceFlag.current) {
        return;
      }
      initDataLayer();
      try {
        if (typeof window === 'undefined' || !window.dataLayer) {
          return;
        }

        const selectedFlight = dynamicData.dynamicFlight?.selectedFlight;
        const selectedHotel = dynamicData.dynamicHotel?.selectedHotel;

        const bounds = selectedFlight ? getBounds(selectedFlight as any) : [];
        const nights = getDaysDifference(
          bounds[0].departureDateTime,
          bounds[bounds.length - 1].arrivalDateTime
        );

        const departureCode = selectedFlight?.outbound?.departureAirport?.code || '';
        const arrivalCode = selectedFlight?.outbound?.arrivalAirport?.code || '';
        const departureDate = bounds[0]?.departureDateTime
          ? dateFormat(bounds[0].departureDateTime, 'dd-MM-yy')
          : '';

        const hotelName = selectedHotel?.item?.name || (selectedHotel as any)?.name || '';
        const id = selectedHotel?.item?.code || (selectedHotel as any)?.itemCode || '';

        const priceValue = dynamicData.packageInfo?.packageTotalPrice ?? 0;
        const price = Number(Number(priceValue).toFixed(2));

        const brand = ((selectedFlight?.airlineCode || 'DYN') + '').replace(/'/g, '');
        const isOneWay = selectedFlight?.routeType === 'OneWay';
        const variant = isOneWay ? 'הלוך' : 'הלוך חזור';

        const data = {
          event: 'productDetails',
          ecommerce: {
            id,
            name: `${hotelName} ${departureCode} ${arrivalCode} ${departureDate}`.trim(),
            category: 'FLT+HTL',
            price,
            brand,
            variant,
            dimension1: 0,
            dimension2: nights,
            dimension3: '',
          },
        };
        window.dataLayer.push(data);
        console.log(data);
      } catch (error) {
        console.error('Error pushing flight product details to dataLayer:', error);
      }
    },
    [initDataLayer]
  );

  const pushDynamicPurchase = useCallback(() => {
    if (!ecommerceFlag.current) {
      return;
    }
    initDataLayer();
    if (typeof window === 'undefined' || !window.dataLayer) {
      return;
    }

    try {
      const orderStr = sessionStorage.getItem('dynSuccessOrder');
      if (!orderStr) {
        console.warn('No dynSuccessOrder found in sessionStorage');
        return;
      }

      const order = JSON.parse(orderStr);

      if (!order || !order.flightInfo || !order.hotelInfo) {
        console.warn('Invalid order structure');
        return;
      }

      const orderId = order.orderId;
      const transactionId = order.transactionId || (orderId ? `RQ${orderId}` : '');
      const trackingKey = `dynPurchaseTracked_${orderId || transactionId}`;

      if (sessionStorage.getItem(trackingKey)) {
        // return;
      }

      const selectedFlight = order.flightInfo.flight;
      const selectedHotel = order.hotelInfo.hotel;

      const bounds = selectedFlight ? getBounds(selectedFlight as any) : [];
      const nights = getDaysDifference(
        bounds[0].departureDateTime,
        bounds[bounds.length - 1].arrivalDateTime
      );

      const departureCode = selectedFlight?.outbound?.departureAirport?.code || '';
      const arrivalCode = selectedFlight?.outbound?.arrivalAirport?.code || '';
      const departureDate = bounds[0]?.departureDateTime
        ? dateFormat(bounds[0].departureDateTime, 'dd-MM-yy')
        : '';

      const hotelName = selectedHotel?.item?.name || (selectedHotel as any)?.name || '';
      const id = selectedHotel?.item?.code || (selectedHotel as any)?.itemCode || '';

      const priceValue = order.totalPrice ?? 0;
      const price = Number(Number(priceValue).toFixed(2));

      const brand = ((selectedFlight?.airlineCode || 'DYN') + '').replace(/'/g, '');
      const isOneWay = selectedFlight?.routeType === 'OneWay';
      const variant = isOneWay ? 'הלוך' : 'הלוך חזור';

      const data = {
        event: 'purchase',
        ecommerce: {
          purchase: {
            actionField: {
              id: order.transactionId || '',
              revenue: order.totalPrice + '',
            },
            products: [
              {
                id,
                name: `${hotelName} ${departureCode} ${arrivalCode} ${departureDate}`.trim(),
                category: 'FLT+HTL',
                price,
                brand,
                variant,
                dimension1: 0,
                dimension2: nights,
                dimension3: '',
                quantity: 1,
                coupon: '',
              },
            ],
          },
        },
      } as EcommercePurchase;
      window.dataLayer.push(data);
      sessionStorage.setItem(trackingKey, 'true');
    } catch (error) {
      console.error('Error pushing flight purchase to dataLayer:', error);
    }
  }, [initDataLayer]);

  return {
    pushImpression,
    pushDynamicProductDetails,
    pushDynamicPurchase,
  };
};

export default useDataLayer;
